import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { platformApi } from './platformApi.js';
import { DataTable } from '@/components/DataTable.jsx';
import { Button } from '@/components/ui/Button.jsx';
import { Badge } from '@/components/ui/Badge.jsx';
import { Dialog } from '@/components/ui/Dialog.jsx';
import { Input } from '@/components/ui/Input.jsx';
import { Plus } from 'lucide-react';

const STATUS_VARIANT = { draft: 'warning', published: 'success', retired: 'default' };

const COLUMNS = [
  { key: 'id', header: 'ID', sortable: true },
  { key: 'name', header: 'Name', sortable: true },
  { key: 'core_version', header: 'Core Version' },
  { key: 'status', header: 'Status', render: (v) => <Badge variant={STATUS_VARIANT[v] ?? 'default'}>{v}</Badge> },
  { key: 'created_at', header: 'Created', render: (v) => v ? new Date(v).toLocaleDateString() : '—' },
];

const EMPTY = { id: '', name: '', coreVersion: '', extensionVersions: '' };

export default function ProfilesPage() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [formError, setFormError] = useState('');
  const [publishing, setPublishing] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ['platform', 'profiles'],
    queryFn: () => platformApi.listProfiles(),
  });

  const create = useMutation({
    mutationFn: (body) => platformApi.createProfile(body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['platform', 'profiles'] }); setOpen(false); setForm(EMPTY); },
    onError: (e) => setFormError(e.message),
  });

  const publish = useMutation({
    mutationFn: (id) => platformApi.publishProfile(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['platform', 'profiles'] }); setPublishing(null); },
  });

  function handleSubmit(e) {
    e.preventDefault();
    setFormError('');
    let extensionVersions = {};
    if (form.extensionVersions.trim()) {
      try { extensionVersions = JSON.parse(form.extensionVersions); }
      catch { setFormError('Extension versions must be valid JSON, e.g. {"payroll":"1.0.0"}'); return; }
    }
    create.mutate({ id: form.id, name: form.name, coreVersion: form.coreVersion, extensionVersions });
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Deployment Profiles</h1>
        <Button size="sm" onClick={() => { setForm(EMPTY); setFormError(''); setOpen(true); }}>
          <Plus className="h-4 w-4" /> New Profile
        </Button>
      </div>

      <DataTable
        columns={[
          ...COLUMNS,
          {
            key: 'actions', header: '', render: (_, row) => row?.status === 'draft' ? (
              <Button size="xs" variant="outline" onClick={(e) => { e.stopPropagation(); setPublishing(row.id); }}>
                Publish
              </Button>
            ) : null,
            accessor: (row) => row,
          },
        ]}
        data={data?.profiles ?? []}
        loading={isLoading}
        enableExport={false}
        rowKey={(r) => r.id}
        emptyMessage="No profiles yet"
      />

      <Dialog open={open} onOpenChange={setOpen} title="New Deployment Profile">
        <form onSubmit={handleSubmit} className="space-y-3 p-4">
          {[
            { name: 'id', label: 'Profile ID', placeholder: 'standard' },
            { name: 'name', label: 'Display Name', placeholder: 'Standard' },
            { name: 'coreVersion', label: 'Core Version', placeholder: '2026.10.0' },
          ].map(({ name, label, placeholder }) => (
            <div key={name} className="space-y-1">
              <label className="text-sm font-medium text-slate-700 dark:text-slate-300">{label}</label>
              <Input value={form[name]} onChange={(e) => setForm((f) => ({ ...f, [name]: e.target.value }))} placeholder={placeholder} required />
            </div>
          ))}
          <div className="space-y-1">
            <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Extension Versions (JSON, optional)</label>
            <Input value={form.extensionVersions} onChange={(e) => setForm((f) => ({ ...f, extensionVersions: e.target.value }))} placeholder='{"payroll":"1.0.0"}' />
          </div>
          {formError && <p className="text-sm text-red-600">{formError}</p>}
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button type="submit" disabled={create.isPending}>{create.isPending ? 'Creating…' : 'Create'}</Button>
          </div>
        </form>
      </Dialog>

      <Dialog open={!!publishing} onOpenChange={(v) => !v && setPublishing(null)} title="Publish Profile">
        <div className="p-4 space-y-4">
          <p className="text-sm text-slate-700 dark:text-slate-300">
            Published profiles are immutable. Customers assigned this profile will use it for all future deployments.
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setPublishing(null)}>Cancel</Button>
            <Button disabled={publish.isPending} onClick={() => publish.mutate(publishing)}>
              {publish.isPending ? 'Publishing…' : 'Publish'}
            </Button>
          </div>
        </div>
      </Dialog>
    </div>
  );
}
